import React, { useState, useRef, useReducer, useCallback, useEffect } from 'react';
import useDidMountEffect from '@use-effect/did-update';
import camelCase from 'camelcase';

import Label from 'Elements/Label';

import {
    Wrapper,
    Container,
    ChevronSVG,
    InputBox,
    DropDown,
    Option,
    OptionLabel
} from './Select.styled';

type Action =
    | { type: 'arrowUp'; maxCount: number }
    | { type: 'arrowDown'; maxCount: number }
    | { type: 'end'; maxCount: number }
    | { type: 'reset' }
    | { type: 'home' };

interface State {
    focusIndex: number;
}

function focusReducer(state: State, action: Action): { focusIndex: number } {
    switch (action.type) {
        case 'arrowUp': {
            const index = state.focusIndex - 1;
            return {
                focusIndex: index < 0 ? action.maxCount - 1 : index
            };
        }
        case 'arrowDown': {
            const index = state.focusIndex + 1;
            return {
                focusIndex: index >= action.maxCount ? 0 : index
            };
        }
        case 'reset': {
            // Set to -1 so first arrow down is 0
            return { focusIndex: -1 };
        }
        case 'home': {
            //set to 1st option focusIndex
            return { focusIndex: 0 };
        }
        case 'end': {
            //set to last option focusIndex
            return { focusIndex: action.maxCount - 1 };
        }
    }
}

export interface Option {
    value: string;
    label: string;
    id: string;
}

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    id: string;
    label?: string;
    options: Option[];
    name: string;
    defaultValue?: string;
    errorMessage?: string;
    handleValueChange?: (value: string | null) => void;
}

const Select: React.FC<Props> = props => {
    const {
        id,
        label,
        options,
        name,
        defaultValue,
        className,
        placeholder = 'Please select an option..',
        handleValueChange,
        'aria-labelledby': ariaLabelledBy,
        ...rest
    } = props;
    const [value, setValue] = useState(defaultValue ? defaultValue : '');
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [{ focusIndex }, dispatch] = useReducer(focusReducer, { focusIndex: -1 });
    const [focusedOption, setFocusedOption] = useState<string | undefined>(undefined);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const selectRef = useRef<HTMLButtonElement>(null);

    const selectedOption = options.find(option => option.value === value) || null;

    const handleOptionClick = (option: Option) => {
        setValue(option.value);
        setIsOpen(false);
    };

    const handleDocumentClick = (event: MouseEvent) => {
        // Fixes bug with select
        if (selectRef.current instanceof HTMLElement) {
            // If click target is not within the Select element, close the dropdown
            if (!selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
    };

    // ensures there is no aria-activedescendant when the dropdown is opened/closed
    useEffect(() => {
        setFocusedOption(undefined);
    }, [isOpen]);

    useEffect(() => {
        // Add event listener to document to handle document clicks
        document.addEventListener('click', handleDocumentClick);

        // Remove event listener on unmount
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);

    // Reset focusIndex on dropdown close
    useEffect(() => {
        if (!isOpen) {
            dispatch({ type: 'reset' });
        }
    }, [isOpen]);

    // Focus dropdown element on focusIndex change if dropdown is open
    useEffect(() => {
        if (isOpen) {
            (dropdownRef.current?.children[focusIndex] as HTMLDivElement).focus();
        } else if (focusIndex >= 0) {
            setValue(
                (dropdownRef.current?.children[focusIndex] as HTMLDivElement).dataset.value || ''
            );
        }
    }, [focusIndex]);

    useEffect(() => {
        if (typeof handleValueChange === 'function') {
            handleValueChange(value || null);
        }
    }, [value]);

    // Update value if defaultValue changes
    useDidMountEffect(() => {
        setValue(defaultValue ? defaultValue : '');
    }, [defaultValue]);

    // Handle keyboard functionality for the box
    const handleBoxKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'Tab') {
                event.preventDefault();
            }

            switch (event.key) {
                case ' ': /* Space */ {
                    setIsOpen(!isOpen);
                    break;
                }
                case 'Enter': {
                    setIsOpen(!isOpen);
                    break;
                }
                case 'Home':
                case 'End':
                case 'ArrowUp':
                case 'ArrowDown': {
                    // TODO Fix performance issue due to needing to update query string after each change
                    dispatch({
                        type: camelCase(event.key),
                        maxCount: dropdownRef.current?.children.length
                    } as Action);
                    break;
                }
            }
        },
        [isOpen]
    );

    // Handle keyboard functionality for the options
    const handleOptionKeyDown = useCallback((event: React.KeyboardEvent) => {
        switch (event.key) {
            case 'Home':
            case 'End':
            case 'ArrowUp':
            case 'ArrowDown': {
                dispatch({
                    type: camelCase(event.key),
                    maxCount: dropdownRef.current?.children.length
                } as Action);
                break;
            }
            case 'Enter':
            case 'Tab': {
                setValue((event.target as HTMLDivElement).dataset.value || '');
                // Hide dropdown
                setIsOpen(false);
                //set selected
                break;
            }
        }
    }, []);

    return (
        <Wrapper className={className}>
            {label && (
                <Label id={`${id}-label`} htmlFor={id}>
                    {label}
                </Label>
            )}
            <Container>
                <input type="hidden" value={value} name={name} {...rest} />
                <InputBox
                    onClick={() => {
                        setIsOpen(!isOpen);
                    }}
                    tabIndex={0}
                    onKeyDown={event => handleBoxKeyDown(event)}
                    id={id}
                    $isOpen={isOpen}
                    aria-label={ariaLabelledBy ? undefined : placeholder}
                    aria-haspopup="listbox"
                    ref={selectRef}
                    aria-expanded={isOpen}
                    aria-labelledby={ariaLabelledBy}
                    style={rest.style}
                    role="combobox"
                    aria-activedescendant={focusedOption ? focusedOption : undefined}
                    aria-controls={`${id}-listbox`}
                >
                    {selectedOption?.label || placeholder}
                    <ChevronSVG $isOpen={isOpen} />
                </InputBox>
                <DropDown
                    id={`${id}-listbox`}
                    tabIndex={-1}
                    $isOpen={isOpen}
                    ref={dropdownRef}
                    aria-expanded={isOpen}
                    aria-label="listbox dropdown"
                    role="listbox"
                    aria-labelledby={ariaLabelledBy}
                >
                    {options.map(option => (
                        <Option
                            key={`option ${option.value}`}
                            id={id + '-' + option.value}
                            data-value={option.value}
                            role="option"
                            tabIndex={-1}
                            onClick={() => handleOptionClick(option)}
                            onKeyDown={event => handleOptionKeyDown(event)}
                            aria-selected={selectedOption?.value === value}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onFocus={() => setFocusedOption(id + '-' + option.value)}
                        >
                            <OptionLabel $isHovered={isHovered} data-hover={isHovered}>
                                {option.label}
                            </OptionLabel>
                        </Option>
                    ))}
                </DropDown>
            </Container>
        </Wrapper>
    );
};

export default Select;
